// Connect Higgsfield (F9b route A): Filmsquare reads the person's own Higgsfield projects with a read-only connection
// in their name, instead of their Claude doing it. One connection per person per workspace, revocable in Settings →
// Connections. The typed calls of core/api/src/routes/integrations.ts, the way back from Higgsfield's sign-in
// (`#…?higgsfield=finish&state=…&code=…` or an error in that fragment), and the states a screen shows quietly:
// not set up on this server, not connected, connected, asks to connect again.
import { type Api, ApiError } from "../api.ts";
import type { Plan } from "./imports-api.ts";

export type HiggsfieldStatus = "none" | "connected" | "needs_reconnect" | "revoked";

/** The caller's own connection. `configured: false` when this server has no key for connections. */
export interface HiggsfieldConnection {
  configured: boolean;
  status: HiggsfieldStatus;
  connectedAt: string | null;
  lastUsedAt: string | null;
  /** the Higgsfield account it reads (its e-mail), when known */
  account: string | null;
}

export interface HiggsfieldProject {
  id: string;
  name: string;
}

export interface HiggsfieldWorkspace {
  id: string;
  /** Higgsfield may send no name for a workspace */
  name: string | null;
  type: string | null;
  projects: HiggsfieldProject[];
}

export type StagedPlan = Plan & { arrangeUrl: string };

/** What a screen does with the connection: four quiet states, nothing loud. */
export type HiggsfieldState = "unconfigured" | "disconnected" | "connected" | "reconnect";

const NOT_CONFIGURED: HiggsfieldConnection = {
  configured: false,
  status: "none",
  connectedAt: null,
  lastUsedAt: null,
  account: null,
};

/** The server answers that connections are not set up (501/503 not_configured), or has no such route at all. */
export function isNotConfigured(e: unknown): boolean {
  if (!(e instanceof ApiError)) return false;
  if (e.code === "not_configured" || e.status === 501) return true;
  return e.status === 404 && /^no route for /i.test(e.message);
}

/** Higgsfield no longer takes the stored sign-in: the person connects again. */
export function isReconnect(e: unknown): boolean {
  return e instanceof ApiError && (e.code === "higgsfield_reconnect" || e.code === "needs_reconnect");
}

/** A connection as the API may send it (camelCase per the spec, snake_case tolerated). */
export function readConnection(v: unknown): HiggsfieldConnection {
  if (!v || typeof v !== "object") return NOT_CONFIGURED;
  const o = v as Record<string, unknown>;
  const s = (a: unknown): string | null => (typeof a === "string" && a ? a : null);
  const status = o.status;
  return {
    configured: o.configured !== false,
    status: status === "connected" || status === "needs_reconnect" || status === "revoked" ? status : "none",
    connectedAt: s(o.connectedAt ?? o.connected_at),
    lastUsedAt: s(o.lastUsedAt ?? o.last_used_at),
    account: s(o.account),
  };
}

/** Where a person signs out of Higgsfield, so the next Connect can sign in to another account. */
export const HIGGSFIELD_SITE = "https://higgsfield.ai";

export function connectionState(c: HiggsfieldConnection | null): HiggsfieldState | null {
  if (!c) return null;
  if (!c.configured) return "unconfigured";
  if (c.status === "connected") return "connected";
  if (c.status === "needs_reconnect") return "reconnect";
  return "disconnected";
}

export function higgsfieldApi(api: Api, ws: string) {
  const w = `/workspaces/${ws}/integrations/higgsfield`;
  return {
    /** the caller's connection; a server without the integration reads as not configured */
    connection: () =>
      api.request<unknown>("GET", w).then(readConnection, (e) => {
        if (isNotConfigured(e)) return NOT_CONFIGURED;
        throw e;
      }),
    /** where to send the browser to sign in to Higgsfield; it comes back to `returnTo` */
    connect: (returnTo: string) =>
      api.request<{ authorizeUrl: string }>("POST", `${w}/connect`, { body: { returnTo } }),
    complete: (state: string, code: string) =>
      api.request<{ connected: true }>("POST", `${w}/complete`, { body: { state, code } }),
    disconnect: () => api.request<unknown>("DELETE", w),
    workspaces: () =>
      api
        .request<{ workspaces: HiggsfieldWorkspace[] }>("GET", `${w}/projects`)
        .then((r) => r.workspaces ?? []),
    /** stage an import plan of these projects, exactly like a plan from Claude */
    stage: (sourceWorkspaceId: string, projectIds: readonly string[]) =>
      api.request<StagedPlan>("POST", `${w}/plans`, { body: { sourceWorkspaceId, projectIds } }),
    /** after Import: Filmsquare reads every run of the plan from Higgsfield (idempotent) */
    read: (planId: string) =>
      api.request<unknown>("POST", `/workspaces/${ws}/import-plans/${planId}/read-from-higgsfield`, {
        body: {},
      }),
  };
}

export type HiggsfieldApi = ReturnType<typeof higgsfieldApi>;

// ── the way back from Higgsfield ───────────────────────────────────────────────────────────────────────────

export type HiggsfieldReturn = { outcome: "connected" } | { outcome: "error"; reason: string | null };
type CapturedHiggsfieldReturn = HiggsfieldReturn | { outcome: "finish"; state: string; code: string };

/**
 * The callback sends the browser to `returnTo` with a sealed-code state to finish, or an error. The
 * studio routes on the hash, so the query may stand before the hash or, appended to a `/#/…` path, inside it.
 */
export function higgsfieldReturn(href: string): CapturedHiggsfieldReturn | null {
  const url = new URL(href);
  const inHash = url.hash.indexOf("?");
  const queries = [
    { params: url.searchParams, finish: false },
    { params: new URLSearchParams(inHash >= 0 ? url.hash.slice(inHash + 1) : ""), finish: true },
  ];
  for (const { params: q, finish } of queries) {
    const v = q.get("higgsfield");
    if (v === "connected") return { outcome: "connected" };
    if (v === "error") return { outcome: "error", reason: q.get("reason") };
    if (finish && v === "finish" && q.get("state") && q.get("code"))
      return { outcome: "finish", state: q.get("state")!, code: q.get("code")! };
  }
  return null;
}

/** The address without `higgsfield` and `reason`, wherever they stood; the rest and the #/route kept. */
export function withoutHiggsfieldParam(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("higgsfield");
  url.searchParams.delete("reason");
  url.searchParams.delete("state");
  url.searchParams.delete("code");
  const at = url.hash.indexOf("?");
  if (at >= 0) {
    const q = new URLSearchParams(url.hash.slice(at + 1));
    q.delete("higgsfield");
    q.delete("reason");
    q.delete("state");
    q.delete("code");
    const rest = q.toString();
    url.hash = url.hash.slice(0, at) + (rest ? `?${rest}` : "");
  }
  return url.toString();
}

/** The web path the callback comes back to: the screen the person is on (`/#/new`), never another origin. */
export function returnPath(hash: string): string {
  const h = hash.replace(/\?.*$/s, "");
  return h && h !== "#" && h !== "#/" ? `/${h.startsWith("#") ? h : `#${h}`}` : "/";
}

let pending: CapturedHiggsfieldReturn | null = null;
/** The screen a sign-in came back to, connected: kept apart from `pending`, which any screen may take and forget. */
let reconnectedRoute: string | null = null;
let finishRoute: string | null = null;
const routeOf = (hash: string) => hash.replace(/\?.*$/s, "") || "#/";
let completing: Promise<void> | null = null;
const returnListeners = new Set<() => void>();

const announceReturn = () => {
  for (const listener of returnListeners) listener();
};

/** Once, before the first route is read: keep what Higgsfield's sign-in came back with and tidy the address. */
export function captureHiggsfieldReturn(): void {
  if (typeof window === "undefined") return;
  const r = higgsfieldReturn(window.location.href);
  if (!r) return;
  pending = r;
  window.history.replaceState(window.history.state, "", withoutHiggsfieldParam(window.location.href));
  const route = routeOf(window.location.hash);
  if (r.outcome === "connected") reconnectedRoute = route;
  if (r.outcome === "finish") finishRoute = route;
  announceReturn();
}

/** What came back from Higgsfield, for the first screen that shows it. */
export const peekHiggsfieldReturn = (): HiggsfieldReturn | null =>
  pending?.outcome === "finish" ? null : pending;

export function onHiggsfieldReturn(listener: () => void): () => void {
  returnListeners.add(listener);
  return () => returnListeners.delete(listener);
}

/** Once the authenticated app knows its workspace, claim the sealed code as this signed-in person. */
export function completeCapturedHiggsfield(hf: HiggsfieldApi): Promise<void> {
  if (pending?.outcome !== "finish") return Promise.resolve();
  if (completing) return completing;
  const state = pending.state;
  const code = pending.code;
  completing = hf
    .complete(state, code)
    .then(() => {
      pending = { outcome: "connected" };
      reconnectedRoute = finishRoute;
    })
    .catch((e) => {
      pending = {
        outcome: "error",
        reason: e instanceof ApiError ? e.code : "complete_failed",
      };
    })
    .finally(() => {
      completing = null;
      announceReturn();
    });
  return completing;
}

/**
 * True once when the sign-in came back connected to this screen (its `#/…` route): the screen that resumes work
 * after a reconnect takes it, whichever other screen already took and forgot the return itself.
 */
export function takeHiggsfieldReconnect(hash: string): boolean {
  if (reconnectedRoute === null || reconnectedRoute !== routeOf(hash)) return false;
  reconnectedRoute = null;
  return true;
}

/** Shown: a later screen does not say it again. */
export function forgetHiggsfieldReturn(): void {
  pending = null;
  announceReturn();
}

/** The callback's reason in the studio's words. */
export function connectErrorText(reason: string | null): string {
  if (reason && /denied|cancel/i.test(reason)) return "Higgsfield was not connected. Nothing was shared.";
  if (reason && /expired|state/i.test(reason))
    return "The sign-in took too long or was used twice. Connect again.";
  return "Higgsfield could not be connected. Try again.";
}

/** "Connected 3 days ago · last read 2 hours ago": the facts of a connection, with an injected clock. */
export function connectionFactsText(c: HiggsfieldConnection, ago: (iso: string) => string): string {
  const parts: string[] = [];
  if (c.connectedAt) parts.push(`Connected ${ago(c.connectedAt)}`);
  if (c.lastUsedAt) parts.push(`last read ${ago(c.lastUsedAt)}`);
  return parts.join(" · ");
}
